/*
  Состояние окна «Какие счета загрузить» (16.10.2026).

  Отметки - по номеру счёта: номер - то, чем счёт назван в файле, и единственное,
  что сервер примет обратно в перечне «загрузить только эти». Окно работает с
  копией выбора: «Отмена» не должна менять того, что было выбрано до открытия.

  Отбор («требуют внимания»), поиск, дом и лист - только то, что ПОКАЗАНО.
  «Отметить показанные» берёт ровно видимое, как «выбрать все» в конвейере
  (решение владельца 09.09.2026): человек видит, что отмечает.

  Дома и листы (19.10.2026) - две раскладки одного вида: слева группа с
  галочкой, справа её счета. Разница одна: дом у счёта один, а листов бывает
  несколько (вкладка «Файлы» приносит счёт и реестром, и начислениями), и такой
  счёт стоит в каждом своём листе.
*/

import { useMemo, useState } from 'react'

export const ALL = '*'
export const ATTENTION = '!'
// Отбор «ФИО расходятся» (20.10.2026, макет «1-fio-B»): человек в файле не тот,
// что по счёту в системе.
export const DIFFERS = '≠'
const NO_HOUSE = ''
export const HOUSES = 'houses'
export const SHEETS = 'sheets'

// Поиск - и по имени из файла, и по должнику из системы: человек ищет того, кого
// видит в строке.
function matches(choice, query) {
  if (!query) return true
  const names = `${choice.person ?? ''} ${choice.debtor ?? ''}`
  const text = `${choice.number} ${names} ${choice.address ?? ''}`.toLowerCase()
  return text.includes(query)
}

// Дом из графы реестра - голый номер («23», «31А», «12/1»), из адреса - уже с
// улицей или словом «Дом» («Дом 23», «ул. Мира, 5», «141551, Московская обл,
// …»): «Дом» приписывается только номеру, иначе выходит «Дом Дом 23».
const BARE_NUMBER = /^\d[\dа-яёa-z/-]*$/i

function houseTitle(key) {
  if (key === NO_HOUSE) return 'Дом не указан'
  return BARE_NUMBER.test(key) ? `Дом ${key}` : key
}

function houseMark(key) {
  if (key === NO_HOUSE) return 'Отметить все счета без дома'
  return BARE_NUMBER.test(key) ? `Отметить весь дом ${key}` : `Отметить все счета: ${key}`
}

function byHouse(filtered) {
  const by = new Map()
  for (const choice of filtered) {
    const key = choice.house ?? NO_HOUSE
    if (!by.has(key)) by.set(key, [])
    by.get(key).push(choice)
  }
  return [...by.entries()]
    .sort(([a], [b]) => a.localeCompare(b, 'ru', { numeric: true }))
    .map(([key, items]) => ({ key, title: houseTitle(key), mark: houseMark(key), items }))
}

// Листы - в порядке файла: первая встреча листа среди счетов.
function bySheet(filtered) {
  const by = new Map()
  for (const choice of filtered) {
    for (const sheet of choice.sheets ?? []) {
      if (!by.has(sheet.key)) by.set(sheet.key, { ...sheet, items: [] })
      by.get(sheet.key).items.push(choice)
    }
  }
  return [...by.values()].map((sheet) => ({ ...sheet, mark: `Отметить все счета: ${sheet.title}` }))
}

export default function usePicker(choices, initial) {
  const [picked, setPicked] = useState(
    () => new Set(initial ?? choices.map((choice) => choice.number)),
  )
  const [view, setView] = useState(ALL)
  const [typed, setTyped] = useState('')
  const [mode, setMode] = useState('list')
  // Выбранная группа - своя у каждой раскладки: переход «по домам» → «по
  // листам» и обратно не теряет, где человек стоял.
  const [at, setAt] = useState({ [HOUSES]: null, [SHEETS]: null })

  const query = typed.trim().toLowerCase()
  const filtered = useMemo(
    () =>
      choices.filter((choice) => {
        if (view === ATTENTION && !choice.attention) return false
        if (view === DIFFERS && !choice.name_differs) return false
        return matches(choice, query)
      }),
    [choices, view, query],
  )

  // Группы считаются по отобранному: отбор «требуют внимания» оставляет только
  // дома и листы, где такие счета есть, и числа у них - про них.
  const groups = useMemo(() => {
    if (mode === HOUSES) return byHouse(filtered)
    if (mode === SHEETS) return bySheet(filtered)
    return null
  }, [mode, filtered])

  const current = groups ? (groups.find((item) => item.key === at[mode]) ?? groups[0]) : null
  const shown = groups ? (current?.items ?? []) : filtered

  function setGroup(key) {
    setAt((before) => ({ ...before, [mode]: key }))
  }

  function mark(items, on) {
    setPicked((before) => {
      const next = new Set(before)
      for (const choice of items) {
        if (on) next.add(choice.number)
        else next.delete(choice.number)
      }
      return next
    })
  }

  return {
    picked,
    view,
    setView,
    typed,
    setTyped,
    mode,
    setMode,
    groups,
    current,
    setGroup,
    shown,
    mark,
  }
}
